import { getMonth, getYear, startOfToday } from 'date-fns';
import { nb } from 'date-fns/locale';

import { format } from '@eika-infrastruktur/date';
import { formatDateToDateMonth } from '@eika-kortbetjening/date-utils';
import { FormattedTransaction } from '@eika/transaction-list/lib/TransactionList';
import { FormattedTransactions } from '@eika/transaction-list/src/TransactionList';

import { groupBy } from '@/utils/array';
import { formatAmount } from '@/utils/text-format-utils';

import { CreditCardReservation, CreditCardTransaction } from '@generated-types';

export type CreditCardMovement = CreditCardTransaction | CreditCardReservation;

const isTransaction = (transaction: CreditCardMovement): transaction is CreditCardTransaction =>
    'postDate' in transaction;

const toUITransaction = (transaction: CreditCardMovement, onClick: (id: string) => void): FormattedTransaction => {
    const reservation = !isTransaction(transaction);
    return {
        id: transaction.id,
        key: transaction.id,
        description: transaction.description,
        date: formatDateToDateMonth(transaction.transactionDate),
        amount: formatAmount(transaction.postAmount, { currencyCode: undefined }),
        reservation,
        deposit: transaction.postAmount > 0,
        onClick,
    };
};

const withReservationsFirst = (transactions: FormattedTransaction[]): FormattedTransaction[] => {
    const reservations = transactions.filter(({ reservation }) => reservation === true);
    const notReservations = transactions.filter(({ reservation }) => !reservation);

    return [...reservations, ...notReservations];
};

const getDate = (transaction: CreditCardMovement) => transaction.transactionDate;

const getId = (transaction: CreditCardMovement) => {
    const date = new Date(getDate(transaction));
    return `${getYear(date)}-${getMonth(date)}`;
};

const sortAndGroupTransactions = (transactions: CreditCardMovement[]) =>
    Object.entries(groupBy(transactions, getId)).sort(([, vals1], [, vals2]) =>
        getDate(vals1[0]) < getDate(vals2[0]) ? 1 : -1,
    );

const getLabel = (transaction: CreditCardMovement) => {
    const capitalizeWord = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);

    const currentYear = getYear(startOfToday());
    const year = getYear(new Date(getDate(transaction)));
    const month = `${capitalizeWord(format(getDate(transaction), 'MMMM', { locale: nb }))}`;

    if (year !== currentYear) {
        return `${month} ${year}`;
    }

    return month;
};

const toTransactionListSections = (
    transactions: CreditCardMovement[],
    onClickTransaction: (id: string) => void,
): FormattedTransactions[] =>
    sortAndGroupTransactions(transactions).map(([key, trans]) => ({
        id: key,
        label: getLabel(trans[0]),
        transactions: withReservationsFirst(trans.map((t) => toUITransaction(t, onClickTransaction))),
    }));

export const TransactionsService = {
    toUITransaction,
    toTransactionListSections,
    isTransaction,
};
